import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Certificate, Education, Experience } from '@/types'

import Image from 'next/image'
import { ArrowUpRight } from 'lucide-react'
import { useRef } from 'react'
import { cms } from '@/lib/cms'
import { formatDate, pad } from '@/lib/format'
import { useIntro } from '@/hooks/use-intro'
import { useReveal } from '@/hooks/use-reveal'
import { ArrowLink, PillLink } from '@/components/generals/arrow-link'
import { Layout } from '@/components/generals/layout'
import { Media } from '@/components/generals/media'
import { DisplayHeading, Eyebrow } from '@/components/generals/section-heading'
import { Seo } from '@/components/generals/seo'
import { SplitWords } from '@/components/generals/split-words'
import { RecLabel, Viewfinder } from '@/components/generals/viewfinder'
import { ExperienceTimeline } from '@/components/landing/experience-timeline'
import { getCertificates, getEducations, getExperiences } from '@/lib/data'
import { siteConfig } from '@/constants/site'
import { media } from '@/constants/media'

import {
    aboutContent,
    heroContent,
    numbers,
    skillGroups
} from '@/constants/content'

interface AboutPageProps {
    experiences: Experience[]
    educations: Education[]
    certificates: Certificate[]
}

const AboutPage: FC<AboutPageProps> = ({ experiences = [], educations = [], certificates = [] }): ReactNode => {
    const heroRef = useRef<HTMLElement>(null)
    const bodyRef = useRef<HTMLDivElement>(null)
    const closingRef = useRef<HTMLDivElement>(null)

    useIntro(heroRef, (timeline, root) => {
        timeline
            .from(root.querySelectorAll('[data-about-title] [data-word]'), { yPercent: 120, duration: 1.5, stagger: 0.1 }, 0)
            .fromTo(root.querySelector('[data-about-frame]'), { clipPath: 'inset(0% 0% 100% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.7, ease: 'expo.inOut' }, 0.2)
            .from(root.querySelectorAll('[data-about-fade]'), { autoAlpha: 0, y: 28, duration: 1.2, stagger: 0.08 }, 0.7)
    })

    useReveal(heroRef)
    useReveal(bodyRef)
    useReveal(closingRef, [educations.length, certificates.length])

    return (
        <Layout>
            <Seo
                title='Tentang saya'
                description='Kenali Iza Faroza, mahasiswa Ilmu Komputer STIKOM El Rahma Bogor, web developer, Pembina Pramuka dan Admin VIP di J&T Express.'
                ogImage={media.portraitFormal.src}
            />

            <section ref={heroRef} id='about-hero' aria-labelledby='about-title' className='relative overflow-hidden bg-ink pb-24 pt-32 text-bone md:pb-36 md:pt-40'>
                <div className='gutter'>
                    <div className='flex items-center justify-between gap-6' data-intro=''>
                        <span data-about-fade=''>
                            <RecLabel label={aboutContent.eyebrow} />
                        </span>

                        <span className='eyebrow text-stone' data-about-fade=''>
                            {siteConfig.location.city} / Aktif sejak {siteConfig.founded}
                        </span>
                    </div>

                    <h1 id='about-title' className='relative z-10 mt-12 text-white mix-blend-difference md:mt-16' data-intro='' data-about-title=''>
                        <span className='block font-wide text-[clamp(4rem,21.5vw,26rem)] font-bold uppercase leading-[0.78] tracking-[-0.065em]'>
                            <SplitWords text={heroContent.lines[0]} />
                        </span>
                        <span className='-mt-[2vw] block text-right font-serif text-[clamp(3.4rem,15vw,18rem)] italic leading-[0.9] tracking-[-0.03em]'>
                            <SplitWords text={heroContent.lines[1]} />
                        </span>
                    </h1>

                    <div className='relative -mt-[6vw] grid gap-10 md:grid-cols-12'>
                        <div className='order-2 flex flex-col gap-6 pt-6 md:order-1 md:col-span-4 md:pt-[14vw]' data-intro=''>
                            <span className='eyebrow text-flare' data-about-fade=''>
                                {aboutContent.kicker}
                            </span>

                            <p className='text-lead text-bone/90' data-about-fade=''>
                                {aboutContent.description}
                            </p>

                            <div className='flex flex-wrap gap-3' data-about-fade=''>
                                <PillLink href='/projects' variant='flare'>
                                    {aboutContent.action}
                                </PillLink>

                                <PillLink href={siteConfig.cvHref} external>
                                    {heroContent.secondaryAction}
                                </PillLink>
                            </div>
                        </div>

                        <div className='order-1 md:order-2 md:col-span-7 md:col-start-6' data-intro='' data-about-frame=''>
                            <Media
                                src={media.portraitFormal.src}
                                alt={media.portraitFormal.alt}
                                sizes='(min-width: 768px) 58vw, 92vw'
                                className='aspect-[4/3]'
                                position='50% 30%'
                                parallax={10}
                                preload
                            >
                                <Viewfinder />
                                <span className='eyebrow absolute bottom-6 left-6 z-10 text-bone'>
                                    {aboutContent.portraitCaption}
                                </span>
                            </Media>
                        </div>
                    </div>
                </div>
            </section>

            <div ref={bodyRef}>
                <section id='principle' aria-labelledby='principle-title' className='bg-bone py-28 text-ink md:py-40'>
                    <div className='gutter'>
                        <div className='grid gap-10 md:grid-cols-12'>
                            <div className='md:col-span-3'>
                                <Eyebrow index='01' className='text-stone-deep'>
                                    {aboutContent.whyLabel}
                                </Eyebrow>
                            </div>

                            <div className='flex flex-col gap-10 md:col-span-9'>
                                <h2 id='principle-title' className='text-[clamp(2.2rem,5.4vw,6rem)] font-medium leading-[1] tracking-[-0.045em]' data-reveal='words'>
                                    <SplitWords text={aboutContent.whyBefore} />{' '}
                                    <SplitWords text={aboutContent.whyAccent} wordClassName='font-serif font-normal italic tracking-[-0.02em]' />{' '}
                                    <SplitWords text={aboutContent.whyAfter} />
                                </h2>

                                <p className='max-w-xl text-lead text-ink/75' data-reveal='fade'>
                                    {aboutContent.whyDescription}
                                </p>
                            </div>
                        </div>

                        <dl className='mt-24 grid grid-cols-1 border-t border-ink/15 sm:grid-cols-3 md:mt-32'>
                            {numbers.map((stat, index) => (
                                <div
                                    key={stat.label}
                                    className='flex flex-col gap-4 border-b border-ink/15 py-8 pr-4 sm:border-b-0 sm:border-r sm:pl-6 sm:first:pl-0 sm:last:border-r-0 lg:py-10'
                                >
                                    <dt className='eyebrow order-2 text-stone-deep'>
                                        {stat.label}
                                    </dt>
                                    <dd className='order-1 font-wide text-[clamp(3rem,6.5vw,6.5rem)] font-semibold leading-[0.85] tracking-[-0.06em]'>
                                        <span data-count={stat.value}>{stat.value}</span>
                                        <span className='text-flare'>{stat.suffix}</span>
                                    </dd>
                                    <span className='eyebrow order-3 text-stone-deep'>
                                        {pad(index + 1)}
                                    </span>
                                </div>
                            ))}
                        </dl>
                    </div>
                </section>

                <section id='story' aria-labelledby='story-title' className='bg-ink py-28 text-bone md:py-40'>
                    <div className='gutter grid gap-14 md:grid-cols-12'>
                        <div className='flex flex-col gap-10 md:col-span-5'>
                            <Eyebrow index='02' className='text-stone'>
                                <span id='story-title'>{aboutContent.storyLabel}</span>
                            </Eyebrow>

                            <DisplayHeading
                                text={aboutContent.storyHeading}
                                accent={aboutContent.storyAccent}
                                className='text-[clamp(2.2rem,4.6vw,5rem)] leading-[0.92]'
                            />

                            <div className='flex flex-col gap-5 text-base leading-relaxed text-bone/80 md:text-lg' data-reveal='stagger'>
                                {aboutContent.story.map(paragraph => (
                                    <p key={paragraph}>
                                        {paragraph}
                                    </p>
                                ))}
                            </div>
                        </div>

                        <div className='flex flex-col gap-10 md:col-span-6 md:col-start-7'>
                            <Media
                                src={media.teaching.src}
                                alt={media.teaching.alt}
                                sizes='(min-width: 768px) 48vw, 92vw'
                                className='aspect-[4/5]'
                                reveal
                                parallax={8}
                            >
                                <span className='eyebrow absolute bottom-6 left-6 z-10 text-bone'>
                                    {aboutContent.activityCaption}
                                </span>
                            </Media>

                            <ol className='grid grid-cols-1 gap-px bg-bone/12 sm:grid-cols-2' data-reveal='stagger' aria-label={aboutContent.skillsLabel}>
                                {skillGroups.map(group => (
                                    <li key={group.index} className='flex flex-col gap-2 bg-ink p-5'>
                                        <span className='eyebrow text-flare'>
                                            {group.index} / {aboutContent.skillsLabel}
                                        </span>
                                        <span className='font-wide text-lg font-semibold uppercase tracking-[-0.02em]'>
                                            {group.title}
                                        </span>
                                        <span className='text-sm text-stone'>
                                            {group.tools.join(', ')}
                                        </span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>
                </section>
            </div>

            <ExperienceTimeline experiences={experiences} index='03' />

            <div ref={closingRef}>
                {educations.length > 0 && (
                    <section id='education' aria-labelledby='education-title' className='bg-bone py-28 text-ink md:py-40'>
                        <div className='gutter'>
                            <div className='grid gap-10 md:grid-cols-12'>
                                <div className='flex flex-col gap-6 md:col-span-5'>
                                    <Eyebrow index='04' className='text-stone-deep'>
                                        {aboutContent.educationLabel}
                                    </Eyebrow>

                                    <h2 id='education-title' className='font-wide text-huge font-semibold uppercase tracking-[-0.055em]' data-reveal='words'>
                                        <SplitWords text={aboutContent.educationTitle} />
                                        <br />
                                        <SplitWords text={aboutContent.educationAccent} wordClassName='font-serif font-normal normal-case italic tracking-[-0.02em]' />
                                    </h2>
                                </div>

                                <div className='flex flex-col justify-end gap-4 md:col-span-6 md:col-start-7' data-reveal='fade'>
                                    <p className='text-lead font-medium'>
                                        {aboutContent.educationDescription}
                                    </p>
                                </div>
                            </div>

                            <ul className='mt-20 border-t border-ink/15 md:mt-28'>
                                {educations.map(education => (
                                    <li key={education.id} className='group border-b border-ink/15'>
                                        <div className='flex flex-col gap-3 py-6 md:grid md:grid-cols-12 md:items-center md:gap-4 md:py-8'>
                                            <span className='eyebrow text-stone-deep md:col-span-2'>
                                                {education.period && (
                                                    <span data-mdlfy-cms={cms('educations', education.id, 'period')}>{education.period}</span>
                                                )}
                                            </span>

                                            <h3 className='font-wide text-[clamp(1.4rem,3vw,3rem)] font-semibold uppercase leading-[0.95] tracking-[-0.04em] md:col-span-5'>
                                                <span data-mdlfy-cms={cms('educations', education.id, 'title')}>{education.title}</span>
                                            </h3>

                                            <div className='flex flex-col gap-1 text-sm text-ink/70 md:col-span-4 md:text-base'>
                                                <span className='flex flex-wrap gap-x-2 font-medium text-ink'>
                                                    {education.field && (
                                                        <span data-mdlfy-cms={cms('educations', education.id, 'field')}>{education.field}</span>
                                                    )}
                                                    {education.field && education.institution && (
                                                        <span aria-hidden='true'>/</span>
                                                    )}
                                                    {education.institution && (
                                                        <span data-mdlfy-cms={cms('educations', education.id, 'institution')}>{education.institution}</span>
                                                    )}
                                                </span>
                                                {education.description && (
                                                    <span data-mdlfy-cms={cms('educations', education.id, 'description')}>{education.description}</span>
                                                )}
                                            </div>

                                            <span className='hidden md:col-span-1 md:block'>
                                                <ArrowUpRight className='ml-auto size-5 text-ink/40 transition-all duration-500 group-hover:rotate-45 group-hover:text-flare' aria-hidden='true' />
                                            </span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </section>
                )}

                {certificates.length > 0 && (
                    <section id='certificates' aria-labelledby='certificates-title' className='bg-ink py-28 text-bone md:py-40'>
                        <div className='gutter'>
                            <div className='flex flex-col gap-8 border-t border-bone/12 pt-10 md:flex-row md:items-end md:justify-between'>
                                <div className='flex flex-col gap-6'>
                                    <Eyebrow index='05' className='text-stone'>
                                        {aboutContent.certificatesLabel}
                                    </Eyebrow>

                                    <h2 id='certificates-title' className='font-wide text-big font-semibold uppercase tracking-[-0.05em]' data-reveal='words'>
                                        <SplitWords text={aboutContent.certificatesTitle} />{' '}
                                        <SplitWords text={aboutContent.certificatesAccent} wordClassName='font-serif font-normal normal-case italic tracking-[-0.02em] text-flare' />
                                    </h2>
                                </div>

                                <div className='flex max-w-xs flex-col gap-6' data-reveal='fade'>
                                    <p className='text-sm leading-relaxed text-stone'>
                                        {aboutContent.certificatesDescription}
                                    </p>

                                    <ArrowLink href='/certificates' className='self-start'>
                                        {aboutContent.certificatesAction}
                                    </ArrowLink>
                                </div>
                            </div>

                            <ul className='mt-16 grid gap-12 md:mt-24 md:grid-cols-3 md:gap-8'>
                                {certificates.map((certificate, index) => (
                                    <li key={certificate.id} className={index === 1 ? 'md:mt-[12vh]' : index === 2 ? 'md:mt-[5vh]' : undefined}>
                                        <article className='group flex flex-col gap-5'>
                                            <div className='relative aspect-[7/5] overflow-hidden bg-ink-raised' data-reveal='image'>
                                                <div className='absolute inset-0' data-reveal-inner=''>
                                                    {certificate.image && (
                                                        <Image
                                                            src={certificate.image}
                                                            alt={certificate.image_alt ?? ''}
                                                            fill
                                                            sizes='(min-width: 768px) 31vw, 92vw'
                                                            className='object-cover transition-transform duration-[1.4s] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-105'
                                                        />
                                                    )}
                                                </div>
                                                <Viewfinder size='sm' className='opacity-0 transition-opacity duration-500 group-hover:opacity-100' />
                                            </div>

                                            <div className='flex items-start justify-between gap-4'>
                                                <div className='flex flex-col gap-2'>
                                                    <h3 className='font-wide text-xl font-semibold uppercase leading-[1] tracking-[-0.03em] md:text-2xl'>
                                                        <span data-mdlfy-cms={cms('certificates', certificate.id, 'title')}>{certificate.title}</span>
                                                    </h3>
                                                    {certificate.issuer && (
                                                        <span className='text-sm text-stone' data-mdlfy-cms={cms('certificates', certificate.id, 'issuer')}>
                                                            {certificate.issuer}
                                                        </span>
                                                    )}
                                                </div>

                                                {certificate.event_date && (
                                                    <time className='eyebrow shrink-0 text-flare' dateTime={certificate.event_date}>
                                                        {formatDate(certificate.event_date)}
                                                    </time>
                                                )}
                                            </div>
                                        </article>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </section>
                )}
            </div>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<AboutPageProps> = async () => {
    const [experiences, educations, certificates] = await Promise.all([
        getExperiences().catch((error: unknown) => {
            console.error('About experiences failed to load:', error instanceof Error ? error.message : error)
            return [] as Experience[]
        }),
        getEducations().catch((error: unknown) => {
            console.error('About educations failed to load:', error instanceof Error ? error.message : error)
            return [] as Education[]
        }),
        getCertificates().catch((error: unknown) => {
            console.error('About certificates failed to load:', error instanceof Error ? error.message : error)
            return [] as Certificate[]
        })
    ])

    const highlighted = [
        ...certificates.filter(certificate => certificate.featured === 1),
        ...certificates.filter(certificate => certificate.featured !== 1)
    ].slice(0, 3)

    return {
        props: { experiences, educations, certificates: highlighted },
        revalidate: 60
    }
}

export default AboutPage
